import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import {
  CreditCard,
  Plus,
  Edit,
  Trash2,
  Loader2,
  CalendarIcon,
  Search,
  Filter,
  Eye,
  Paperclip,
  FileText,
  Image,
  X,
  Download,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';

import { API } from '../utils/api';

const Checkbook = () => {
  const { llcId: paramLLCId } = useParams();
  const [searchParams] = useSearchParams();
  const { getAuthHeaders, user } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [llcs, setLLCs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [selectedLLC, setSelectedLLC] = useState(paramLLCId || '');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [dateFilter, setDateFilter] = useState(null);
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [attachmentDialogOpen, setAttachmentDialogOpen] = useState(false);
  const [viewingTransaction, setViewingTransaction] = useState(null);
  const [formData, setFormData] = useState({
    llc_id: paramLLCId || '',
    date: format(new Date(), 'yyyy-MM-dd'),
    description: '',
    amount: '',
    category_id: '',
    vendor_id: '',
    reference: '',
    notes: ''
  });

  const fetchData = async () => {
    try {
      const [llcsRes, categoriesRes, vendorsRes] = await Promise.all([
        axios.get(`${API}/llcs`, { headers: getAuthHeaders() }),
        axios.get(`${API}/categories`, { headers: getAuthHeaders() }),
        axios.get(`${API}/vendors`, { headers: getAuthHeaders() })
      ]);
      setLLCs(llcsRes.data);
      setCategories(categoriesRes.data);
      setVendors(vendorsRes.data);

      if (selectedLLC) {
        const txnRes = await axios.get(`${API}/transactions?llc_id=${selectedLLC}`, { headers: getAuthHeaders() });
        setTransactions(txnRes.data);
      } else {
        const txnRes = await axios.get(`${API}/transactions`, { headers: getAuthHeaders() });
        setTransactions(txnRes.data);
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedLLC]);

  useEffect(() => {
    if (paramLLCId) {
      setSelectedLLC(paramLLCId);
      setFormData(prev => ({ ...prev, llc_id: paramLLCId }));
    }
  }, [paramLLCId]);

  const handleSubmit = async (e, saveAndNew = false) => {
    e.preventDefault();
    setFormLoading(true);
    
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        vendor_id: formData.vendor_id || null
      };

      let txnId;
      if (selectedTransaction) {
        await axios.put(`${API}/transactions/${selectedTransaction.id}`, payload, { headers: getAuthHeaders() });
        txnId = selectedTransaction.id;
        toast.success('Transaction updated successfully');
      } else {
        const response = await axios.post(`${API}/transactions`, payload, { headers: getAuthHeaders() });
        txnId = response.data.id;
        toast.success('Transaction created successfully');
      }
      
      // Upload any pending attachments
      if (attachmentFiles.length > 0 && txnId) {
        for (const file of attachmentFiles) {
          const formData = new FormData();
          formData.append('file', file);
          try {
            await axios.post(`${API}/transactions/${txnId}/attachments`, formData, {
              headers: {
                ...getAuthHeaders(),
                'Content-Type': 'multipart/form-data'
              }
            });
          } catch (err) {
            toast.error(`Failed to upload ${file.name}`);
          }
        }
      }
      
      fetchData();
      
      if (saveAndNew && !selectedTransaction) {
        // Reset form but keep dialog open for new entry
        // Keep the LLC selection for convenience
        const currentLLC = formData.llc_id;
        setSelectedTransaction(null);
        setAttachmentFiles([]);
        setFormData({
          llc_id: currentLLC,
          date: format(new Date(), 'yyyy-MM-dd'),
          description: '',
          amount: '',
          category_id: '',
          vendor_id: '',
          reference: '',
          notes: ''
        });
      } else {
        setDialogOpen(false);
        resetForm();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save transaction');
    } finally {
      setFormLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
    const maxSize = 10 * 1024 * 1024; // 10MB
    
    const validFiles = files.filter(file => {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name}: Invalid file type. Only JPG, PNG, PDF allowed.`);
        return false;
      }
      if (file.size > maxSize) {
        toast.error(`${file.name}: File too large. Max 10MB.`);
        return false;
      }
      return true;
    });
    
    const totalFiles = attachmentFiles.length + validFiles.length;
    if (totalFiles > 5) {
      toast.error('Maximum 5 attachments allowed per transaction');
      return;
    }
    
    setAttachmentFiles([...attachmentFiles, ...validFiles]);
  };

  const removeAttachmentFile = (index) => {
    setAttachmentFiles(attachmentFiles.filter((_, i) => i !== index));
  };

  const uploadAttachmentToExisting = async (txnId, file) => {
    setUploadingAttachment(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(`${API}/transactions/${txnId}/attachments`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Attachment uploaded successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload attachment');
    } finally {
      setUploadingAttachment(false);
    }
  };

  const deleteAttachment = async (txnId, attachmentId) => {
    try {
      await axios.delete(`${API}/transactions/${txnId}/attachments/${attachmentId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Attachment deleted');
      fetchData();
      // Update viewing transaction
      if (viewingTransaction && viewingTransaction.id === txnId) {
        setViewingTransaction({
          ...viewingTransaction,
          attachments: viewingTransaction.attachments.filter(a => a.id !== attachmentId)
        });
      }
    } catch (error) {
      toast.error('Failed to delete attachment');
    }
  };

  const downloadAttachment = async (filename) => {
    const win = window.open('', '_blank');
    try {
      const res = await axios.get(`${API}/attachments/${filename}`, { headers: getAuthHeaders() });
      win.location.href = res.data.url;
    } catch (error) {
      win?.close();
      toast.error(error.response?.data?.detail || 'Failed to open attachment');
    }
  };

  const handleEdit = (txn) => {
    setSelectedTransaction(txn);
    setFormData({
      llc_id: txn.llc_id,
      date: txn.date,
      description: txn.description,
      amount: txn.amount.toString(),
      category_id: txn.category_id,
      vendor_id: txn.vendor_id || '',
      reference: txn.reference || '',
      notes: txn.notes || ''
    });
    setAttachmentFiles([]);
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedTransaction) return;
    
    try {
      await axios.delete(`${API}/transactions/${selectedTransaction.id}`, { headers: getAuthHeaders() });
      toast.success('Transaction deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedTransaction(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete transaction');
    }
  };

  const resetForm = () => {
    setSelectedTransaction(null);
    setAttachmentFiles([]);
    setFormData({
      llc_id: selectedLLC || '',
      date: format(new Date(), 'yyyy-MM-dd'),
      description: '',
      amount: '',
      category_id: '',
      vendor_id: '',
      reference: '',
      notes: ''
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    // Handle ISO datetime strings (with T) vs date-only strings
    const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    const [year, month, day] = datePart.split('-').map(Number);
    // Compact format like a checkbook: MM/DD/YY
    const shortYear = year.toString().slice(-2);
    return `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${shortYear}`;
  };

  const getLLCName = (llcId) => {
    const llc = llcs.find(l => l.id === llcId);
    return llc?.name || 'Unknown';
  };

  const filteredTransactions = transactions.filter(txn => {
    const matchesSearch = txn.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      txn.category_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !categoryFilter || txn.category_type === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedTransactions = [...filteredTransactions].sort((a, b) => {
    let aVal = a[sortConfig.key];
    let bVal = b[sortConfig.key];
    
    if (aVal === null || aVal === undefined) aVal = '';
    if (bVal === null || bVal === undefined) bVal = '';
    
    if (typeof aVal === 'string') aVal = aVal.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal.toLowerCase();
    
    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const SortIcon = ({ column }) => {
    if (sortConfig.key === column) {
      return sortConfig.direction === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />;
    }
    return <ArrowUpDown className="w-4 h-4 opacity-50" />;
  };

  // Calculate running balance - checkbook style
  // Only include transactions that affect the bank account:
  // - Checking category (current_asset where name is "Checking")
  // - Income (deposits to bank)
  // - Expense (payments from bank)
  // - Liability payments from checkbook (non-journal entry payments)
  // - Equity transactions from checkbook (owner's draw, distributions)

  const isAdmin = user?.role === 'superadmin' || user?.role === 'admin';
  const canEditTransactions = user?.role === 'superadmin' || user?.role === 'manager';

  // Helper function to render pending files
  const renderPendingFiles = () => {
    if (attachmentFiles.length === 0) return null;
    const items = [];
    for (let i = 0; i < attachmentFiles.length; i++) {
      const file = attachmentFiles[i];
      const isPdf = file.type === 'application/pdf';
      items.push(
        <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
          <div className="flex items-center gap-2">
            {isPdf ? <FileText className="w-4 h-4 text-red-500" /> : <Image className="w-4 h-4 text-blue-500" />}
            <span className="truncate max-w-[180px]">{file.name}</span>
            <span className="text-xs text-slate-400">({(file.size / 1024).toFixed(0)}KB)</span>
          </div>
          <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeAttachmentFile(i)}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      );
    }
    return <div className="space-y-1 mt-2">{items}</div>;
  };

  // Helper function to render existing attachments
  const renderExistingAttachments = () => {
    if (!selectedTransaction?.attachments?.length) return null;
    const items = [];
    for (let i = 0; i < selectedTransaction.attachments.length; i++) {
      const att = selectedTransaction.attachments[i];
      const isPdf = att.content_type === 'application/pdf';
      items.push(
        <div key={att.id} className="flex items-center justify-between p-2 bg-emerald-50 rounded text-sm">
          <div className="flex items-center gap-2">
            {isPdf ? <FileText className="w-4 h-4 text-red-500" /> : <Image className="w-4 h-4 text-blue-500" />}
            <span className="truncate max-w-[180px]">{att.original_name}</span>
          </div>
          <div className="flex gap-1">
            <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => downloadAttachment(att.filename)}>
              <Download className="w-4 h-4 text-blue-500" />
            </Button>
            <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => deleteAttachment(selectedTransaction.id, att.id)}>
              <Trash2 className="w-4 h-4 text-rose-500" />
            </Button>
          </div>
        </div>
      );
    }
    return <div className="mt-3"><p className="text-xs font-medium mb-1">Existing:</p><div className="space-y-1">{items}</div></div>;
  };

  // Helper function for dialog attachments
  const renderDialogAttachments = () => {
    if (!viewingTransaction?.attachments?.length) {
      return <p className="text-sm text-slate-500 py-4 text-center">No attachments yet</p>;
    }
    const items = [];
    for (let i = 0; i < viewingTransaction.attachments.length; i++) {
      const att = viewingTransaction.attachments[i];
      const isPdf = att.content_type === 'application/pdf';
      items.push(
        <div key={att.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded flex items-center justify-center ${isPdf ? 'bg-red-100' : 'bg-blue-100'}`}>
              {isPdf ? <FileText className="w-5 h-5 text-red-600" /> : <Image className="w-5 h-5 text-blue-600" />}
            </div>
            <div>
              <p className="text-sm font-medium truncate max-w-[200px]">{att.original_name}</p>
              <p className="text-xs text-slate-500">{(att.size / 1024).toFixed(1)} KB</p>
            </div>
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => downloadAttachment(att.filename)}>
              <Download className="w-4 h-4 text-blue-600" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => deleteAttachment(viewingTransaction.id, att.id)}>
              <Trash2 className="w-4 h-4 text-rose-600" />
            </Button>
          </div>
        </div>
      );
    }
    return <div className="space-y-2">{items}</div>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-amber-700" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="checkbook-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-serif font-semibold text-slate-900">
            Transaction Register
          </h1>
          <p className="text-slate-500 mt-1">
            {canEditTransactions 
              ? 'Record and manage transactions across your LLCs'
              : 'View transactions across your LLCs (Read Only)'}
          </p>
        </div>
        {canEditTransactions && (
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-transaction-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Transaction
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle className="font-serif text-xl">
                  {selectedTransaction ? 'Edit Transaction' : 'New Transaction'}
                </DialogTitle>
                <DialogDescription>
                  {selectedTransaction ? 'Update transaction details' : 'Record a new transaction'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-2 gap-4 py-4">
                  <div className="col-span-2 space-y-2">
                    <Label>LLC *</Label>
                    <Select
                      value={formData.llc_id}
                      onValueChange={(value) => setFormData({ ...formData, llc_id: value })}
                    >
                      <SelectTrigger data-testid="txn-llc-select">
                        <SelectValue placeholder="Select LLC" />
                      </SelectTrigger>
                      <SelectContent>
                        {llcs.map((llc) => (
                          <SelectItem key={llc.id} value={llc.id}>{llc.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Date *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                          data-testid="txn-date-btn"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.date ? (() => {
                            const [y, m, d] = formData.date.split('-').map(Number);
                            return format(new Date(y, m - 1, d), 'PPP');
                          })() : 'Pick a date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={formData.date ? (() => {
                            const [y, m, d] = formData.date.split('-').map(Number);
                            return new Date(y, m - 1, d);
                          })() : undefined}
                          onSelect={(date) => setFormData({ ...formData, date: date ? format(date, 'yyyy-MM-dd') : '' })}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Amount * <span className="text-xs text-slate-500">(+ for deposit, - for payment)</span></Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="-100.00 or +500.00"
                      required
                      data-testid="txn-amount-input"
                    />
                  </div>
                  
                  <div className="col-span-2 space-y-2">
                    <Label>Description *</Label>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Rent payment for January"
                      required
                      data-testid="txn-description-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Vendor/Customer</Label>
                    <Select
                      value={formData.vendor_id || "none"}
                      onValueChange={(value) => {
                        const newVendorId = value === "none" ? "" : value;
                        const selectedVendor = vendors.find(v => v.id === newVendorId);
                        // Auto-fill category if vendor has a default category
                        if (selectedVendor?.default_category_id) {
                          setFormData({ ...formData, vendor_id: newVendorId, category_id: selectedVendor.default_category_id });
                        } else {
                          setFormData({ ...formData, vendor_id: newVendorId });
                        }
                      }}
                    >
                      <SelectTrigger data-testid="txn-vendor-select">
                        <SelectValue placeholder="Select vendor/customer (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {vendors.map((v) => (
                          <SelectItem key={v.id} value={v.id}>
                            <span className="capitalize text-xs text-slate-500">[{v.vendor_type}]</span> {v.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Category *</Label>
                    <Select
                      value={formData.category_id}
                      onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                    >
                      <SelectTrigger data-testid="txn-category-select">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            <span className="capitalize">{cat.category_type}</span>: {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Reference #</Label>
                    <Input
                      value={formData.reference}
                      onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                      placeholder="Check #, Invoice #, etc."
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Additional notes..."
                      rows={2}
                    />
                  </div>
                  
                  {/* Attachments Section - Simplified */}
                  <div className="col-span-2 space-y-2">
                    <Label>Attachments (JPG, PNG, PDF - Max 5 files)</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        id="attachment-input"
                        multiple
                        accept=".jpg,.jpeg,.png,.pdf"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => document.getElementById('attachment-input').click()}
                        disabled={attachmentFiles.length >= 5}
                      >
                        <Paperclip className="w-4 h-4 mr-1" />
                        Add Files ({attachmentFiles.length}/5)
                      </Button>
                    </div>
                    {renderPendingFiles()}
                    {renderExistingAttachments()}
                  </div>
                </div>
                <DialogFooter className="gap-2 sm:gap-0">
                  <Button type="button" variant="outline" onClick={() => {
                    setDialogOpen(false);
                    resetForm();
                  }}>
                    Cancel
                  </Button>
                  {!selectedTransaction && (
                    <Button
                      type="button"
                      variant="outline"
                      className="border-amber-300 text-amber-700 hover:bg-amber-50"
                      disabled={formLoading}
                      onClick={(e) => handleSubmit(e, true)}
                      data-testid="txn-save-new-btn"
                    >
                      {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save & New'}
                    </Button>
                  )}
                  <Button
                    type="submit"
                    className="bg-slate-900 hover:bg-slate-800"
                    disabled={formLoading}
                    data-testid="txn-submit-btn"
                  >
                    {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (selectedTransaction ? 'Update' : 'Save & Close')}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">LLC</Label>
              <Select value={selectedLLC || "all"} onValueChange={(v) => setSelectedLLC(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-llc-select">
                  <SelectValue placeholder="All LLCs" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All LLCs</SelectItem>
                  {llcs.map((llc) => (
                    <SelectItem key={llc.id} value={llc.id}>{llc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Category Type</Label>
              <Select value={categoryFilter || "all"} onValueChange={(v) => setCategoryFilter(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-category-select">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="income">Income</SelectItem>
                  <SelectItem value="expense">Expense</SelectItem>
                  <SelectItem value="asset">Asset</SelectItem>
                  <SelectItem value="liability">Liability</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="sm:col-span-2">
              <Label className="text-xs text-slate-500 mb-1.5 block">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search transactions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="txn-search-input"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transactions Table */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="font-serif">
            Transaction Register
            {!canEditTransactions && <Badge variant="outline" className="ml-2 text-slate-500"><Eye className="w-3 h-3 mr-1" />Read Only</Badge>}
          </CardTitle>
          <CardDescription>
            {filteredTransactions.length} transaction{filteredTransactions.length !== 1 ? 's' : ''}
            {selectedLLC && ` for ${getLLCName(selectedLLC)}`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredTransactions.length === 0 ? (
            <div className="text-center py-12">
              <CreditCard className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="font-serif text-lg font-semibold text-slate-700">No transactions</h3>
              <p className="text-slate-500 mt-1">
                {searchQuery || categoryFilter 
                  ? 'Try different filters' 
                  : canEditTransactions 
                    ? 'Start by adding your first transaction'
                    : 'No transactions have been recorded yet'}
              </p>
            </div>
          ) : (
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="max-h-[600px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-800 sticky top-0 z-10">
                    <tr>
                      <th className="text-left text-white font-semibold px-3 py-2 cursor-pointer hover:bg-slate-700 whitespace-nowrap" onClick={() => handleSort('date')}>
                        <div className="flex items-center gap-1">Date <SortIcon column="date" /></div>
                      </th>
                      {!selectedLLC && <th className="text-left text-white font-semibold px-3 py-2 cursor-pointer hover:bg-slate-700 whitespace-nowrap" onClick={() => handleSort('llc_id')}>
                        <div className="flex items-center gap-1">LLC <SortIcon column="llc_id" /></div>
                      </th>}
                      <th className="text-left text-white font-semibold px-3 py-2 cursor-pointer hover:bg-slate-700" onClick={() => handleSort('description')}>
                        <div className="flex items-center gap-1">Description <SortIcon column="description" /></div>
                      </th>
                      <th className="text-left text-white font-semibold px-3 py-2 cursor-pointer hover:bg-slate-700 whitespace-nowrap" onClick={() => handleSort('category_name')}>
                        <div className="flex items-center gap-1">Category <SortIcon column="category_name" /></div>
                      </th>
                      <th className="text-left text-white font-semibold px-3 py-2 cursor-pointer hover:bg-slate-700 whitespace-nowrap" onClick={() => handleSort('reference')}>
                        <div className="flex items-center gap-1">Reference <SortIcon column="reference" /></div>
                      </th>
                      <th className="text-right text-white font-semibold px-3 py-2 cursor-pointer hover:bg-slate-700 whitespace-nowrap" onClick={() => handleSort('amount')}>
                        <div className="flex items-center justify-end gap-1">Amount <SortIcon column="amount" /></div>
                      </th>
                      {canEditTransactions && <th className="w-16 px-2 py-2"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedTransactions.map((txn) => (
                      <tr key={txn.id} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
                        <td className="text-slate-700 px-3 py-1.5 whitespace-nowrap">{formatDate(txn.date)}</td>
                        {!selectedLLC && (
                          <td className="text-slate-600 px-3 py-1.5 whitespace-nowrap">
                            {getLLCName(txn.llc_id)}
                          </td>
                        )}
                        <td className="px-3 py-1.5">
                          <span className="text-slate-800">{txn.description}</span>
                          {txn.vendor_name && (
                            <span className="text-slate-400 ml-2">• {txn.vendor_name}</span>
                          )}
                          {canEditTransactions && txn.attachments && txn.attachments.length > 0 && (
                            <Paperclip className="w-3 h-3 text-blue-500 inline ml-2" />
                          )}
                        </td>
                        <td className="px-3 py-1.5 whitespace-nowrap">
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            txn.category_type === 'income' ? 'bg-emerald-100 text-emerald-700' :
                            txn.category_type === 'expense' ? 'bg-rose-100 text-rose-700' :
                            txn.category_type === 'asset' ? 'bg-blue-100 text-blue-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {txn.category_name}
                          </span>
                        </td>
                        <td className="text-slate-600 px-3 py-1.5 whitespace-nowrap">
                          {txn.reference || '-'}
                        </td>
                        <td className={`text-right px-3 py-1.5 whitespace-nowrap ${
                          txn.amount >= 0 ? 'text-emerald-600' : 'text-rose-600'
                        }`}>
                          {formatCurrency(txn.amount)}
                        </td>
                        {canEditTransactions && (
                          <td className="px-2 py-1.5">
                            <div className="flex gap-0.5 justify-end">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => {
                                  setViewingTransaction(txn);
                                  setAttachmentDialogOpen(true);
                                }}
                                title="Attachments"
                              >
                                <Paperclip className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => handleEdit(txn)}
                              >
                                <Edit className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-rose-600 hover:text-rose-700"
                                onClick={() => {
                                  setSelectedTransaction(txn);
                                  setDeleteDialogOpen(true);
                                }}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Transaction?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this transaction. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedTransaction(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-rose-600 hover:bg-rose-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Attachment Management Dialog */}
      <Dialog open={attachmentDialogOpen} onOpenChange={(open) => {
        setAttachmentDialogOpen(open);
        if (!open) setViewingTransaction(null);
      }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Manage Attachments</DialogTitle>
            <DialogDescription>{viewingTransaction?.description}</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Add New Attachment</Label>
              <div className="flex items-center gap-2">
                <input type="file" id="existing-attachment-input" accept=".jpg,.jpeg,.png,.pdf" className="hidden"
                  onChange={(e) => {
                    if (e.target.files[0] && viewingTransaction) {
                      uploadAttachmentToExisting(viewingTransaction.id, e.target.files[0]);
                      e.target.value = '';
                    }
                  }}
                />
                <Button variant="outline" size="sm" disabled={uploadingAttachment || (viewingTransaction?.attachments?.length >= 5)}
                  onClick={() => document.getElementById('existing-attachment-input').click()}>
                  {uploadingAttachment ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                  Upload ({viewingTransaction?.attachments?.length || 0}/5)
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Current Attachments</Label>
              {renderDialogAttachments()}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAttachmentDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Checkbook;
